/**
 * Slug-based tab deep-linking for admin add/edit forms. Encapsulates the
 * resolve-from-hash + `history.replaceState` + hash-sync boilerplate that was
 * copy-pasted into every tabbed form.
 *
 * Tabs become addressable via `#slug` so links/bookmarks land on the right tab,
 * and the browser back button keeps the active tab in sync. For mode-specific
 * tabs (e.g. an edit-only tab) pass the already-filtered slug list - the index
 * positions must match the rendered tab order.
 *
 *   const TAB_SLUGS = ['details', 'visibility'] as const
 *   const { tabValue, setTabValue, handleTabChange } = useFormTabs(TAB_SLUGS)
 */
import { useEffect, useState, type SyntheticEvent } from 'react'
import { useLocation } from 'react-router-dom'

export interface UseFormTabsResult {
  tabValue: number
  setTabValue: (index: number) => void
  handleTabChange: (event: SyntheticEvent, next: number) => void
}

export interface UseFormTabsOptions {
  /** Tab index to use when the URL has no matching hash (e.g. a legacy
   *  `?tab=N` param or a non-zero default). Defaults to 0. */
  fallbackIndex?: number
}

export function useFormTabs(
  slugs: readonly string[],
  options: UseFormTabsOptions = {},
): UseFormTabsResult {
  const location = useLocation()
  // Stable dependency for the sync effect when callers pass a fresh array.
  const slugsKey = slugs.join('|')

  const [tabValue, setTabValue] = useState(() => {
    const hash = location.hash.replace(/^#/, '').toLowerCase()
    const idx = slugs.indexOf(hash)
    return idx >= 0 ? idx : options.fallbackIndex ?? 0
  })

  const handleTabChange = (_event: SyntheticEvent, next: number) => {
    setTabValue(next)
    const slug = slugs[next]
    if (slug) window.history.replaceState(null, '', `#${slug}`)
  }

  // Follow external hash changes (back/forward, or landing with a hash).
  useEffect(() => {
    const hash = location.hash.replace(/^#/, '').toLowerCase()
    const idx = slugs.indexOf(hash)
    if (idx >= 0) setTabValue((prev) => (prev === idx ? prev : idx))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, slugsKey])

  return { tabValue, setTabValue, handleTabChange }
}

export default useFormTabs
